import { IconNames } from '@blueprintjs/icons';
import { sortAsc } from '@strata/helper-functions';
import { GridWithFilters, PrimaryData, SecondaryData } from '@strata/react-grid';
import { Icon } from '@strata/react-status-icons';
import { SuccessToast } from '@strata/react-toast-message';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { ListPageHeader } from 'components/ListPageHeader';
import { DeleteItemDialog } from 'components/valueCase/DeleteItemDialog';
import { FundingSourcesForDisplay } from 'components/valueCase/helpers';
import { handleError } from 'services/ErrorManagement';
import { get, httpDelete, useGet } from 'services/Http';

const fetchValueCases = async () => {
    try {
        const response = await get('/performance/admin/value_cases');
        return (response ?? []).map((vc) => ({
            ...vc,
            product_service_incentives: vc.product_service_incentives?.map(
                (psi) => psi.product_service_oid,
            ),
            diagnoses: vc.diagnoses?.map((dx) => dx.dx_oid),
        }));
    } catch (err) {
        handleError('Error fetching data', err);
        return undefined;
    }
};

const deleteValueCase = async (valueCaseId) => {
    try {
        await httpDelete(`/performance/admin/value_cases/${valueCaseId}`);
        SuccessToast({ message: 'Value case was deleted successfully.' });
    } catch (err) {
        handleError('Error deleting value case.', err);
    }
};

const columns = ({ navigate, setShowDeleteValueCaseDialog }) => [
    {
        header: 'Value Case Name',
        accessorKey: 'value_case_name',
        size: 200,
        cell: ({ getValue }) => <PrimaryData data={getValue()} />,
    },
    {
        header: 'Product/Service OIDs',
        accessorKey: 'product_service_incentives',
        size: 100,
        cell: ({ getValue }) => <SecondaryData data={getValue()?.join(', ')} />,
    },
    {
        header: 'Diagnoses',
        accessorKey: 'diagnoses',
        size: 100,
        cell: ({ getValue }) => <SecondaryData data={getValue()?.join(', ')} />,
    },
    {
        header: 'Stratification Rule',
        accessorKey: 'strat_rule_display_name',
        size: 100,
    },
    {
        header: 'Performance Requirement Name',
        accessorKey: 'performance_requirement_name',
        size: 150,
    },
    {
        header: 'Payer ID',
        accessorKey: 'payer_id',
        size: 60,
    },
    {
        header: 'Segment ID',
        accessorKey: 'segment_id',
        size: 70,
    },
    {
        header: 'Funding Source',
        accessorKey: 'funding_source',
        size: 80,
        cell: ({ getValue }) => FundingSourcesForDisplay[getValue()] ?? getValue(),
    },
    {
        header: 'Actions',
        accessorKey: 'actions',
        size: 70,
        enableSorting: false,
        cell: ({ row }) => (
            <div className="sds_filter-list__actions">
                <Icon
                    icon={IconNames.EDIT}
                    intent={Icon.Intent.SECONDARY}
                    title="Edit"
                    onClick={() =>
                        navigate({
                            pathname: `/value-case/${row.original.value_case_id}`,
                        })
                    }
                />
                <Icon
                    icon={IconNames.TRASH}
                    intent={Icon.Intent.SECONDARY}
                    title="Delete"
                    onClick={() => {
                        setShowDeleteValueCaseDialog({
                            value_case_id: row.original.value_case_id,
                            value_case_name: row.original.value_case_name,
                        });
                    }}
                />
            </div>
        ),
    },
];

export const ValueCaseList = () => {
    const [showDeleteValueCaseDialog, setShowDeleteValueCaseDialog] = useState();
    const navigate = useNavigate();

    const [valueCases, setValueCases] = useState();
    useEffect(() => {
        void (async () => {
            setValueCases(await fetchValueCases());
        })();
    }, []);

    const payers = useGet('/reference/payers/');
    const payerSegments = useGet('/reference/payer_segments');

    const segmentIdToNameMap = Object.fromEntries(
        (payerSegments ?? []).map((segment) => [
            segment.segment_id,
            `${segment.segment_name} - ${segment.segment_id}`,
        ]),
    );

    const payerIdToNameMap = Object.fromEntries(
        (payers ?? []).map((payer) => [payer.payer_id, `${payer.payer_name} - ${payer.payer_id}`]),
    );

    const filterCategories = useMemo(
        () => [
            {
                key: 'product_service_incentives',
                title: 'Product/Service OIDs',
                customValues: [
                    ...new Set(
                        (valueCases ?? []).map((vc) => vc.product_service_incentives).flat(),
                    ).values(),
                ]
                    .sort(sortAsc)
                    .map((i) => ({
                        value: i,
                        handler: (psi) => psi.includes(i),
                    })),
            },
            {
                key: 'diagnoses',
                title: 'Diagnoses',
                customValues: [
                    ...new Set((valueCases ?? []).map((vc) => vc.diagnoses).flat()).values(),
                ]
                    .sort(sortAsc)
                    .map((d) => ({
                        value: d,
                        handler: (dx) => dx.includes(d),
                    })),
            },
            {
                key: 'strat_rule_display_name',
                title: 'Stratification Rule',
            },
            {
                key: 'payer_id',
                title: 'Payer ID',
                valueDisplayTextMap: payerIdToNameMap,
            },
            {
                key: 'segment_id',
                title: 'Segment ID',
                valueDisplayTextMap: segmentIdToNameMap,
            },
            {
                key: 'funding_source',
                title: 'Funding Source',
                valueDisplayTextMap: FundingSourcesForDisplay,
            },
        ],
        [valueCases, payerIdToNameMap, segmentIdToNameMap],
    );

    return (
        <div className="sds_filter-list">
            <DeleteItemDialog
                itemToDelete={DeleteItemDialog.Items.VALUE_CASE}
                isOpen={!!showDeleteValueCaseDialog}
                onCancel={() => setShowDeleteValueCaseDialog()}
                onDelete={async () => {
                    if (showDeleteValueCaseDialog) {
                        await deleteValueCase(showDeleteValueCaseDialog.value_case_id);
                    }
                    setShowDeleteValueCaseDialog();
                    setValueCases(await fetchValueCases());
                }}
                itemPreview={showDeleteValueCaseDialog?.value_case_name}
            />
            <GridWithFilters
                headerContent={
                    <ListPageHeader
                        pageTitle="Value Cases"
                        buttons={[{ label: 'Create Value Case', path: '/value-case/' }]}
                    />
                }
                enableSearch
                isLoading={!valueCases}
                filterCategories={filterCategories}
                sortProperties={[{ id: 'value_case_name', desc: false }]}
                data={valueCases}
                columns={columns({ navigate, setShowDeleteValueCaseDialog })}
            />
        </div>
    );
};
